import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Box, CircularProgress, Typography, TextField, Button, Alert, Link } from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { motion } from 'framer-motion'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import EmailIcon from '@mui/icons-material/Email'
import { useAppSelector, useAppDispatch } from '../hooks'
import { setAuthenticated, setUserData } from '../stores/UserStore'
import type { UserData, UserRole } from '../types/user'
import authService from '../services/authService'
import Parse from 'parse'
import { getServerBaseUrl } from '@/utils/serverUrl'
import AuthSplitCard from './auth/AuthSplitCard'
import { useLightWorkspaceTheme } from './auth/AuthLightSurface'

interface EmailVerificationPageProps {
  onSuccess?: () => void
}

export default function EmailVerificationPage({ onSuccess }: EmailVerificationPageProps) {
  // The card is light in either workspace mode (AuthSplitCard), so its colours come from the light theme.
  const { theme } = useLightWorkspaceTheme()
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null)
  const [userEmail, setUserEmail] = useState<string>('')
  const [logoLoaded, setLogoLoaded] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  
  const dispatch = useAppDispatch()
  const settings = useAppSelector((state) => state.workspace.settings)
  const platformName = settings?.platform_name || 'Sevv'
  const logoUrl = '/assets/images/SVGLogo.svg'

  // Get current user email on mount
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        let currentUser = authService.getCurrentUser()
        if (!currentUser) {
          currentUser = await authService.checkSession()
        }
        if (currentUser?.email) {
          setUserEmail(currentUser.email)
        }
      } catch (error) {
        console.error('Error getting current user:', error)
      }
    }
    getCurrentUser()
  }, [])

  // The server marks the user verified with the master key, so the SDK's stored current user keeps
  // `emailVerified: false` until it is fetched. MainMenu and AuthenticationController read that
  // copy and would send the user straight back here.
  const leaveVerifiedPage = async ({ refreshUser = true } = {}) => {
    if (refreshUser) {
      try {
        await Parse.User.current()?.fetch()
      } catch (refreshError) {
        console.error('[EmailVerificationPage] Could not refresh the signed-in user:', refreshError)
      }
    }
    if (onSuccess) {
      onSuccess()
    } else {
      window.location.href = '/'
    }
  }

  // Auto-send the verification code on first visit per session. This is the only
  // sender in the browser — signup used to send too, which is how two codes went
  // out with only the second working (SC-385).
  //
  // `/send` rather than `/resend` because the two now mean different things: /send
  // is idempotent and means "make sure a live code exists", so the cloud function's
  // send at signup and this one collapse into a single email. /resend always mints
  // a new code and 429s inside its cooldown, which is the Resend button below.
  // (The previous note here said /send was chosen to avoid the 60s gate — that
  // bypass was the bug, not the reason.)
  useEffect(() => {
    const autoSend = async () => {
      let sessionKey: string | null = null
      try {
        let currentUser = authService.getCurrentUser()
        if (!currentUser) {
          currentUser = await authService.checkSession()
        }
        if (!currentUser?.id) return

        // Already verified (a tab that came back here, or a stale redirect): nothing to send.
        if (Parse.User.current()?.get('emailVerified') === true) {
          await leaveVerifiedPage({ refreshUser: false })
          return
        }

        sessionKey = `sevv_email_verify_autosend_${currentUser.id}`
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(sessionKey)) {
          return
        }
        // Claimed before the request, with no await since the check: StrictMode runs this effect
        // twice in development, and two sends in flight together both mint a code on the server.
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(sessionKey, '1')
        }

        const baseUrl = getServerBaseUrl()
        const isDev = !!((import.meta as any).env && (import.meta as any).env.DEV)
        const fullUrl = isDev
          ? '/api/auth/email-verification/send'
          : `${baseUrl}/api/auth/email-verification/send`

        const sessionToken = currentUser.sessionToken || Parse.User.current()?.getSessionToken()

        const response = await fetch(fullUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` }),
          },
          credentials: isDev ? 'same-origin' : 'include',
          body: JSON.stringify(currentUser.email ? { email: currentUser.email } : {}),
        })

        const data = await response.json()

        if (data.alreadyVerified) {
          await leaveVerifiedPage()
          return
        }

        if (data.success) {
          const cooldown = typeof data.retryAfter === 'number' ? data.retryAfter : 60
          setResendCooldown(cooldown)
        } else if (typeof data.retryAfter === 'number') {
          setResendCooldown(data.retryAfter)
        }
      } catch (err) {
        console.error('[EmailVerificationPage] Auto-send error:', err)
        // Nothing reached the user, so the next visit may try again.
        if (sessionKey && typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem(sessionKey)
        }
      }
    }

    void autoSend()
  }, [])

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  // Handle code input change
  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return
    }

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)
    setError(null)
    setRemainingAttempts(null)

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  // Handle backspace
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLDivElement | HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    const newCode = [...code]
    
    for (let i = 0; i < 6; i++) {
      newCode[i] = pastedData[i] || ''
    }
    
    setCode(newCode)
    setError(null)
    
    // Focus the next empty input or the last one
    const nextEmptyIndex = newCode.findIndex(digit => !digit)
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex
    inputRefs.current[focusIndex]?.focus()
  }

  // Verify code
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setRemainingAttempts(null)

    // Check if all digits are filled
    const codeString = code.join('')
    if (codeString.length !== 6) {
      setError('Please enter the complete 6-digit code')
      return
    }

    setIsLoading(true)

    try {
      const baseUrl = getServerBaseUrl()
      const isDev = !!((import.meta as any).env && (import.meta as any).env.DEV)
      const fullUrl = isDev 
        ? '/api/auth/email-verification/verify' 
        : `${baseUrl}/api/auth/email-verification/verify`

      // Get session token for authentication
      const currentUser = await authService.getCurrentUser()
      const sessionToken = currentUser?.sessionToken || Parse.User.current()?.getSessionToken()

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` }),
        },
        credentials: isDev ? 'same-origin' : 'include',
        body: JSON.stringify({ code: codeString }),
      })

      const data = await response.json()

      if (data.success) {
        setSuccess(true)
        
        // Refresh user data to get updated emailVerified status
        try {
          const updatedUser = await authService.getCurrentUser()
          if (updatedUser) {
            const userData: UserData = {
              id: updatedUser.id,
              email: updatedUser.email,
              username: updatedUser.username,
              role: (updatedUser.role || 'user') as UserRole,
              createdAt: updatedUser.createdAt?.toISOString() || new Date().toISOString(),
              firstName: updatedUser.firstName || '',
              lastName: updatedUser.lastName || '',
              title: updatedUser.title || '',
              contact: updatedUser.contact || '',
              bio: updatedUser.bio || '',
              avatar: updatedUser.avatar || 'adam',
              gameCharacter: updatedUser.gameCharacter || 'adam',
              customAvatarUrl: updatedUser.customAvatarUrl || '',
              profileCompleted: updatedUser.profileCompleted || false,
              emailVerified: true,
            }
            dispatch(setUserData(userData))
            dispatch(setAuthenticated(true))
          }
        } catch (refreshError) {
          console.error('Error refreshing user data:', refreshError)
        }

        // Auto-redirect after 2 seconds
        setTimeout(() => {
          void leaveVerifiedPage()
        }, 2000)
      } else {
        setError(data.error || 'Invalid verification code. Please try again.')
        if (data.remainingAttempts !== undefined) {
          setRemainingAttempts(data.remainingAttempts)
        }
        // Clear code on error
        setCode(['', '', '', '', '', ''])
        inputRefs.current[0]?.focus()
      }
    } catch (err: any) {
      console.error('Verification error:', err)
      setError(err.message || 'Failed to verify code. Please try again.')
      setCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } finally {
      setIsLoading(false)
    }
  }

  // Resend code
  const handleResend = async () => {
    if (resendCooldown > 0 || isResending) {
      return
    }

    setIsResending(true)
    setError(null)

    try {
      const baseUrl = getServerBaseUrl()
      const isDev = !!((import.meta as any).env && (import.meta as any).env.DEV)
      const fullUrl = isDev 
        ? '/api/auth/email-verification/resend' 
        : `${baseUrl}/api/auth/email-verification/resend`

      // Get session token for authentication
      const currentUser = await authService.getCurrentUser()
      const sessionToken = currentUser?.sessionToken || Parse.User.current()?.getSessionToken()

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` }),
        },
        credentials: isDev ? 'same-origin' : 'include',
        body: JSON.stringify(userEmail ? { email: userEmail } : {}),
      })

      const data = await response.json()

      if (data.success) {
        // Set cooldown (60 seconds minimum, or use retryAfter if provided)
        const cooldown = data.retryAfter ? data.retryAfter : 60
        setResendCooldown(cooldown)
        setError(null)
        // Show success message briefly
        setTimeout(() => {
          // Message will be shown via the button state
        }, 100)
      } else {
        // If rate limited, set cooldown from retryAfter
        if (data.retryAfter) {
          setResendCooldown(data.retryAfter)
        }
        setError(data.error || 'Failed to resend code. Please try again later.')
      }
    } catch (err: any) {
      console.error('Resend error:', err)
      setError(err.message || 'Failed to resend code. Please try again later.')
    } finally {
      setIsResending(false)
    }
  }

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])



  const logoBadgeSx = useMemo<SxProps<Theme>>(
    () => ({
      width: '60px',
      height: '60px',
      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
      borderRadius: '12px',
      mb: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.3)}`,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
        opacity: 0.8,
      },
    }),
    [theme]
  )

  if (success) {
    return (
      <AuthSplitCard screen="verify">
        <Box
          sx={{
            textAlign: 'center',
            my: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box 
            sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3
            }}
          >
            <Box
              component={motion.div}
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: logoLoaded ? 1 : 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              sx={logoBadgeSx}
            >
              <Box
                component="img"
                src={logoUrl}
                alt={`${platformName} Logo`}
                onLoad={() => setLogoLoaded(true)}
                sx={{
                  width: '40px',
                  height: '40px',
                  objectFit: 'contain',
                  position: 'relative',
                  zIndex: 1,
                  filter: 'brightness(0) invert(1)'
                }}
              />
            </Box>
          </Box>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
            Email Verified!
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
            Your email address has been verified successfully. Redirecting you to the dashboard...
          </Typography>
        </Box>
      </AuthSplitCard>
    )
  }

  return (
    // SC-438: the code screen shares the sign-in dialog's split layout and brand panel.
    <AuthSplitCard screen="verify">
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {/* Logo Container */}
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 4,
            mt: 2
          }}
        >
          <Box
            component={motion.div}
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: logoLoaded ? 1 : 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            sx={logoBadgeSx}
          >
            <Box
              component="img"
              src={logoUrl}
              alt={`${platformName} Logo`}
              onLoad={() => setLogoLoaded(true)}
              sx={{
                width: '40px',
                height: '40px',
                objectFit: 'contain',
                position: 'relative',
                zIndex: 1,
                filter: 'brightness(0) invert(1)'
              }}
            />
          </Box>
          <Box
            component={motion.div}
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                mb: 1, 
                fontWeight: 600, 
                color: 'text.primary',
                fontSize: '1.75rem',
                textAlign: 'center'
              }}
            >
              Verify your email
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '0.875rem',
                textAlign: 'center'
              }}
            >
              {userEmail ? `Enter the 6-digit code sent to ${userEmail}` : 'Enter the 6-digit code sent to your email'}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.error.main, 0.08),
              backgroundImage: 'none',
              border: `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
              color: 'text.primary',
              '& .MuiAlert-icon': {
                color: 'error.main',
              },
            }}
          >
            <ErrorOutlineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {error}
            {remainingAttempts !== null && remainingAttempts > 0 && (
              <Typography variant="body2" sx={{ mt: 1, fontSize: '0.875rem' }}>
                {remainingAttempts} attempt{remainingAttempts !== 1 ? 's' : ''} remaining
              </Typography>
            )}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleVerify}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
          }}
        >
          {/* 6-digit code input */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1.5,
              mb: 3
            }}
          >
            {code.map((digit, index) => (
              <TextField
                key={index}
                inputRef={(el) => (inputRefs.current[index] = el)}
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                inputProps={{
                  maxLength: 1,
                  style: {
                    textAlign: 'center',
                    fontSize: '24px',
                    fontWeight: 600,
                    padding: '12px',
                  }
                }}
                sx={{
                  width: '56px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: error ? 'error.main' : 'divider',
                      borderWidth: '2px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: error ? 'error.main' : alpha(theme.palette.primary.main, 0.5),
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: error ? 'error.main' : 'primary.main',
                      borderWidth: '2px',
                    },
                  },
                }}
              />
            ))}
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={isLoading || code.join('').length !== 6}
            sx={{
              py: 1.5,
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              minHeight: '44px',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              color: 'primary.contrastText',
              boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                boxShadow: `0 10px 25px 0 ${alpha(theme.palette.primary.main, 0.4)}`,
                transform: 'translateY(-1px)',
              },
              '&:disabled': {
                background: alpha(theme.palette.primary.main, 0.3),
                color: 'text.disabled',
                boxShadow: 'none',
                transform: 'none',
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: 'primary.contrastText' }} />
            ) : (
              'Verify & continue'
            )}
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Didn't receive the code?
          </Typography>
          <Button
            variant="outlined"
            onClick={handleResend}
            disabled={resendCooldown > 0 || isResending}
            startIcon={<EmailIcon />}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              color: resendCooldown > 0 ? 'text.disabled' : 'primary.main',
              borderColor: resendCooldown > 0 ? 'divider' : 'primary.main',
              '&:hover': {
                backgroundColor:
                  resendCooldown > 0 ? 'transparent' : alpha(theme.palette.primary.main, 0.08),
                backgroundImage: 'none',
                borderColor: resendCooldown > 0 ? 'divider' : 'primary.dark',
                color: resendCooldown > 0 ? 'text.disabled' : 'primary.dark',
              },
              '&:disabled': {
                borderColor: 'divider',
                color: 'text.disabled',
              },
            }}
          >
            {isResending ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: 'primary.main' }} />
                Sending...
              </>
            ) : resendCooldown > 0 ? (
              `Resend Code (${resendCooldown}s)`
            ) : (
              'Resend Code'
            )}
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Link
            component="button"
            type="button"
            onClick={async () => {
              try {
                console.log('[EmailVerificationPage] Logging out user before redirecting to sign in')
                // Log out user completely
                await authService.signOut()
                // Clear Redux state
                dispatch(setAuthenticated(false))
                dispatch(setUserData(null))
                // Redirect to home (which will show sign in dialog)
                window.location.href = '/'
              } catch (error) {
                console.error('[EmailVerificationPage] Error during logout:', error)
                // Still redirect even if logout fails
                dispatch(setAuthenticated(false))
                dispatch(setUserData(null))
                window.location.href = '/'
              }
            }}
            sx={{ 
              textDecoration: 'none', 
              color: 'primary.main',
              fontWeight: 500,
              fontSize: '0.875rem',
              cursor: 'pointer',
              '&:hover': {
                color: 'primary.dark',
                textDecoration: 'underline',
              },
            }}
          >
            Back to Sign In
          </Link>
        </Box>
      </Box>
    </AuthSplitCard>
  )
}

